import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { memo, type ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClosedPosition, ClosingReason } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { Amount } from '../../components/Amount';
import { StatusChip, type StatusTone } from '../../components/StatusChip';
// Direct file imports, not the positions index: PositionsScreen mounts this tab, so importing the
// index would be an import cycle (import-x/no-cycle).
import { PositionTimeline } from '../positions/PositionTimeline';
import { formatDecimal } from '../positions/formatters';
import { formatClockTime } from '../shared/format-time';

/**
 * Chip tone of each close reason (C3 R12: features map their states to a tone). The mockup draws
 * the channel in blue (info), the market (SL, TP) in grey (neutral) and the trader in amber
 * (warning). Proposal, to be validated: the emergency stop is a trader action too (warning), and an
 * unknown reason stays neutral.
 */
const REASON_TONE: Record<ClosingReason, StatusTone> = {
  CLOSED_BY_CHANNEL: 'info',
  CLOSED_BY_STOP_LOSS: 'neutral',
  CLOSED_BY_TAKE_PROFIT: 'neutral',
  CLOSED_BY_TRADER: 'warning',
  CLOSED_BY_EMERGENCY_STOP: 'warning',
  CLOSE_REASON_UNKNOWN: 'neutral',
};

/** Between the open and close times (mockup: "09:14 → 16:58"); the same in FR and EN. */
const TIME_ARROW = ' → ';

/** Sizes in px drawn on mockup Historique (`docs/06-MiniApp/Maquettes/Historique.html`). */
const MOCKUP = {
  /** Side badge: 11px bold, fixed width so the instruments line up. */
  sideSize: 11,
  sideWidth: '46px',
  /** Lots, channel and times line. */
  smallSize: 13,
  /** Timeline heading, as on E8. */
  headingSize: 12,
  /** Row padding: 12px top and bottom, 14px left and right (MUI spacing unit = 8px). */
  paddingY: 1.5,
  paddingX: 1.75,
} as const;

/**
 * One closed position of E9 (M6 R1–R6, mockup Historique): side, instrument, lots, channel, open →
 * close times, close-reason chip (`t('reason.<code>')`, C1 decision 1, C2 R10), the rounded-close
 * note when there is one (R4), and the result. Tapping it opens the M5 timeline of that position
 * (R6, I10); the timeline is mounted only while open, so reopening fetches it again. Memoised:
 * "Show more" and each refresh re-render the list, while a row's data rarely changes.
 */
export const HistoryRow = memo(function HistoryRow({
  position,
}: {
  readonly position: ClosedPosition;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const titleId = useId();
  const detailsId = useId();

  return (
    <Box
      component="article"
      aria-labelledby={titleId}
      sx={(theme) => ({
        px: MOCKUP.paddingX,
        py: MOCKUP.paddingY,
        '&:not(:last-of-type)': { borderBottom: `1px solid ${theme.fx.neutralBg}` },
      })}
    >
      {/* WAI-ARIA accordion: the heading holds the button that opens the timeline. */}
      <Box component="h3" id={titleId} sx={{ m: 0, font: 'inherit' }}>
        <ButtonBase
          onClick={() => {
            setExpanded((open) => !open);
          }}
          aria-expanded={expanded}
          aria-controls={expanded ? detailsId : undefined}
          sx={{
            width: '100%',
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textAlign: 'left',
            borderRadius: '8px',
          }}
        >
          <SideBadge side={position.side} />
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            <Box component="span" sx={{ display: 'block' }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {position.symbol}
              </Box>{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: NUMERIC_FONT,
                  fontSize: MOCKUP.smallSize,
                  color: 'text.secondary',
                }}
              >
                {t('positions.lots', { lots: formatDecimal(position.lots, language, 2) })}
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: MOCKUP.smallSize,
                color: 'text.secondary',
                overflowWrap: 'anywhere',
              }}
            >
              {/* The channel's name even after the trader removed it (R5): it comes with the row. */}
              {position.channel.title} ·{' '}
              <time dateTime={position.openedAt}>
                {formatClockTime(position.openedAt, language)}
              </time>
              {TIME_ARROW}
              <time dateTime={position.closedAt}>
                {formatClockTime(position.closedAt, language)}
              </time>
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              <StatusChip tone={REASON_TONE[position.closeReason]}>
                {t(`reason.${position.closeReason}`)}
              </StatusChip>
            </Box>
          </Box>
          <Amount {...position.result} signed />
          <ExpandMoreOutlined
            aria-hidden
            fontSize="small"
            sx={{ color: 'text.secondary', transform: expanded ? 'rotate(180deg)' : 'none' }}
          />
        </ButtonBase>
      </Box>

      {/* FXM-04 §6 case 12: a rounded partial close is stated, not hidden (R4, I7). */}
      {position.notes.map((note, index) => (
        <Box
          key={`${String(index)}-${note}`}
          component="p"
          sx={{
            m: 0,
            mt: 0.75,
            fontSize: MOCKUP.smallSize,
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          {t(`reason.${note}`)}
        </Box>
      ))}

      {expanded && (
        <Box id={detailsId} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1.25 }}>
          <Box
            component="h4"
            sx={{
              m: 0,
              fontSize: MOCKUP.headingSize,
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t('positions.timeline.title')}
          </Box>
          <PositionTimeline positionId={position.id} />
        </Box>
      )}
    </Box>
  );
});

/** BUY on green, SELL on red, white text, fixed width (mockup Historique). */
function SideBadge({ side }: { readonly side: ClosedPosition['side'] }): ReactElement {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={(theme) => ({
        flexShrink: 0,
        width: MOCKUP.sideWidth,
        textAlign: 'center',
        fontSize: MOCKUP.sideSize,
        fontWeight: 700,
        color: theme.fx.surface,
        bgcolor: side === 'BUY' ? theme.fx.success : theme.fx.danger,
        py: 0.375,
        borderRadius: '6px',
      })}
    >
      {t(`positions.side.${side}`)}
    </Box>
  );
}
